import React, { useState, useEffect, useCallback } from 'react';
import { paymentService } from '../../services/paymentService';

const TEST_PLAN_DATA = {
    id: 2,
    plan_name: 'Basic Plan',
    base_price: 49.99,
    user_limit: 20,
    ai_tokens_limit: 100000,
    billing_cycle: 'Monthly'
};

const TEST_CUSTOMER_DATA = {
    client_name: 'Test Company',
    contact_name: 'John Doe',
    contact_email: 'john@test.com',
    contact_phone: '+1234567890'
};

const PaymentTest = () => {
    const [testResults, setTestResults] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const runTests = useCallback(async () => {
        setIsLoading(true);
        const results = {};

        try {
            // Test 1: Country Detection
            console.log('Testing country detection...');
            const country = await paymentService.detectUserCountry();
            results.countryDetection = {
                success: true,
                country: country,
                gateway: country === 'IN' ? 'razorpay' : 'stripe'
            };
            console.log('Country detection result:', results.countryDetection);

            // Test 2: Payment Session Creation
            console.log('Testing payment session creation...');
            const sessionResult = await paymentService.createPaymentSession(TEST_PLAN_DATA, TEST_CUSTOMER_DATA);
            results.sessionCreation = {
                success: sessionResult.success,
                gateway: sessionResult.gateway,
                data: sessionResult.data,
                error: sessionResult.error
            };
            console.log('Session creation result:', results.sessionCreation);

            // Test 3: API Endpoints Check
            console.log('Testing API endpoints...');
            const endpoints = [
                '/api/payments/create-stripe-session',
                '/api/payments/create-razorpay-order',
                '/api/payments/confirm-stripe-payment',
                '/api/payments/verify-razorpay-payment'
            ];

            const endpointResults = {};
            for (const endpoint of endpoints) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}${endpoint}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ test: true })
                    });
                    endpointResults[endpoint] = {
                        status: response.status,
                        available: response.status !== 404
                    };
                } catch (error) {
                    endpointResults[endpoint] = {
                        status: 'error',
                        available: false,
                        error: error.message
                    };
                }
            }
            results.endpoints = endpointResults;

        } catch (error) {
            console.error('Test error:', error);
            results.error = error.message;
        }

        setTestResults(results);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        runTests();
    }, [runTests]);

    return (
        <div style={{ padding: '20px', fontFamily: 'monospace', maxWidth: '1000px' }}>
            <h2>Payment Integration Test</h2>
            
            <div style={{ marginBottom: '20px' }}>
                <button 
                    onClick={runTests} 
                    disabled={isLoading}
                    style={{ padding: '10px 20px', marginRight: '10px' }}
                >
                    {isLoading ? 'Running Tests...' : 'Run Tests Again'}
                </button>
            </div>

            {/* Environment Variables */}
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5' }}>
                <h3>Environment Configuration:</h3>
                <p><strong>Backend URL:</strong> {process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}</p>
                <p><strong>Stripe Key:</strong> {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? '✅ Set' : '❌ Not Set'}</p>
                <p><strong>Razorpay Key:</strong> {process.env.REACT_APP_RAZORPAY_KEY_ID ? '✅ Set' : '❌ Not Set'}</p>
            </div>

            {/* Test Results */}
            {Object.keys(testResults).length > 0 && (
                <div>
                    {/* Country Detection */}
                    {testResults.countryDetection && (
                        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: testResults.countryDetection.success ? '#e6ffe6' : '#ffe6e6' }}>
                            <h3>Country Detection:</h3>
                            <p><strong>Country:</strong> {testResults.countryDetection.country}</p>
                            <p><strong>Selected Gateway:</strong> {testResults.countryDetection.gateway}</p>
                            <p><strong>Status:</strong> {testResults.countryDetection.success ? '✅ Success' : '❌ Failed'}</p>
                        </div>
                    )}

                    {/* Session Creation */}
                    {testResults.sessionCreation && (
                        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: testResults.sessionCreation.success ? '#e6ffe6' : '#ffe6e6' }}>
                            <h3>Payment Session Creation:</h3>
                            <p><strong>Gateway:</strong> {testResults.sessionCreation.gateway}</p>
                            <p><strong>Status:</strong> {testResults.sessionCreation.success ? '✅ Success' : '❌ Failed'}</p>
                            {testResults.sessionCreation.error && (
                                <p><strong>Error:</strong> {testResults.sessionCreation.error}</p>
                            )}
                            {testResults.sessionCreation.data && (
                                <details>
                                    <summary>Response Data</summary>
                                    <pre>{JSON.stringify(testResults.sessionCreation.data, null, 2)}</pre>
                                </details>
                            )}
                        </div>
                    )}

                    {/* API Endpoints */}
                    {testResults.endpoints && (
                        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f0f8ff' }}>
                            <h3>API Endpoints Status:</h3>
                            {Object.entries(testResults.endpoints).map(([endpoint, result]) => (
                                <div key={endpoint} style={{ marginBottom: '10px' }}>
                                    <p>
                                        <strong>{endpoint}:</strong> 
                                        <span style={{ color: result.available ? 'green' : 'red', marginLeft: '10px' }}>
                                            {result.available ? '✅ Available' : '❌ Not Available'}
                                        </span>
                                        {result.status && <span style={{ marginLeft: '10px' }}>(Status: {result.status})</span>}
                                    </p>
                                    {result.error && (
                                        <p style={{ color: 'red', fontSize: '12px' }}>Error: {result.error}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Error */}
                    {testResults.error && (
                        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#ffe6e6' }}>
                            <h3>Test Error:</h3>
                            <p style={{ color: 'red' }}>{testResults.error}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Test Data */}
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fff3cd' }}>
                <h3>Test Data Used:</h3>
                <details>
                    <summary>Plan Data</summary>
                    <pre>{JSON.stringify(TEST_PLAN_DATA, null, 2)}</pre>
                </details>
                <details>
                    <summary>Customer Data</summary>
                    <pre>{JSON.stringify(TEST_CUSTOMER_DATA, null, 2)}</pre>
                </details>
            </div>

            {/* Instructions */}
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#d1ecf1' }}>
                <h3>Next Steps:</h3>
                <ol>
                    <li>Ensure all environment variables are set in your <code>.env</code> file</li>
                    <li>Verify your backend server is running on the correct port</li>
                    <li>Test the complete payment flow by selecting a plan and going through registration</li>
                    <li>Use the test cards provided in the documentation</li>
                </ol>
            </div>
        </div>
    );
};

export default PaymentTest; 